/**
 * Centralized Notification Navigation & Formatting Helpers (Phase 3)
 * Maps backend action_type and action_data payloads to existing React frontend routes.
 */

export const formatNotificationTime = (isoString) => {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs < 0) return "Just now";

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // Standard format: DD/MM/YYYY hh:mm A
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
  } catch {
    return "";
  }
};

export const getModuleBadgeColor = (module) => {
  const norm = (module || "").toUpperCase();
  switch (norm) {
    case "PERMIT":
    case "PTW":
      return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    case "SAFETY":
      return "bg-amber-500/20 text-amber-500 border-amber-500/30";
    case "NCR":
      return "bg-rose-500/20 text-rose-500 border-rose-500/30";
    case "QUALITY":
    case "QC":
      return "bg-blue-500/20 text-blue-500 border-blue-500/30";
    default:
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  }
};

/**
 * Dispatches navigation based on notification payload.
 * Supports both signatures: (notification, navigate) or (navigate, notification).
 */
export const handleNotificationAction = (arg1, arg2) => {
  let notification = null;
  let navigate = null;

  if (typeof arg1 === "function") {
    navigate = arg1;
    notification = arg2;
  } else {
    notification = arg1;
    navigate = arg2;
  }

  if (!notification || typeof navigate !== "function") return;

  const actionType = notification.action_type || "";
  const actionData = notification.action_data || {};

  try {
    switch (actionType) {
      case "OPEN_SAFETY_CHECKLIST": {
        const checklistId = actionData.checklist_id || notification.resource_id;
        const projectId = actionData.project_id || notification.project_id;
        const view = actionData.view || "";

        if (view === "report" && checklistId) {
          navigate(`/safety/inspection-report/${checklistId}`);
        } else if (checklistId && projectId) {
          navigate(`/safety/inspection-checker?project_id=${projectId}&checklist_id=${checklistId}`);
        } else if (checklistId) {
          navigate(`/safety/inspection-checker?checklist_id=${checklistId}`);
        } else {
          navigate("/safety/inspection-checker");
        }
        break;
      }

      case "OPEN_SAFETY_TEMPLATE": {
        navigate("/safetySetup");
        break;
      }

      case "OPEN_SAFETY_OBSERVATION": {
        const obsId = actionData.observation_id || notification.resource_id;
        const projectId = actionData.project_id || notification.project_id;
        if (obsId && projectId) {
          navigate(`/safety/observations?project_id=${projectId}&observation_id=${obsId}`);
        } else if (obsId) {
          navigate(`/safety/observations?observation_id=${obsId}`);
        } else {
          navigate("/safety/observations");
        }
        break;
      }

      case "OPEN_OBSERVATION": {
        const obsId = actionData.observation_id || notification.resource_id;
        const projectId = actionData.project_id || notification.project_id;
        if (obsId && projectId) {
          navigate(`/safety/observations?project_id=${projectId}&observation_id=${obsId}`);
        } else {
          navigate("/safety/observations");
        }
        break;
      }

      case "OPEN_NCR": {
        const ncrId = actionData.ncr_id || notification.resource_id;
        if (ncrId) {
          navigate(`/ncr/${ncrId}`);
        } else {
          navigate("/NCR");
        }
        break;
      }

      case "OPEN_PERMIT": {
        const permitId = actionData.permit_id || notification.resource_id;
        const projectId = actionData.project_id || notification.project_id;
        if (permitId) {
          const query = projectId ? `?permitId=${permitId}&projectId=${projectId}` : `?permitId=${permitId}`;
          navigate(`/safety/permit-to-work/create${query}`);
        } else {
          navigate("/safety/permit");
        }
        break;
      }

      default: {
        if (actionData.url) {
          navigate(actionData.url);
        } else if ((notification.module || "").toUpperCase() === "SAFETY") {
          navigate("/safety/observations");
        }
        break;
      }
    }
  } catch (err) {
    console.error("[NotificationNavigation] Navigation failed:", err);
  }
};
